<template>
  <DefaultLayout>
    <div class="mt-4 space-y-6">
      <!-- Filters Section -->
      <div
        class="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-inner dark:shadow-gray-900 border border-gray-100 dark:border-gray-700/60 p-6"
      >
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">الفلاتر</h2>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <!-- Year Selector -->
          <div>
            <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-right"
              >السنة</label
            >
            <select
              v-model="pendingYear"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:border-primary"
            >
              <option v-for="year in availableYears" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>

          <!-- Period Selector -->
          <div>
            <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-right">
              الفترة ضمن السنة
            </label>
            <select
              v-model="pendingPeriod"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:border-primary"
            >
              <!-- <option value="all">جميع الأوقات</option> -->
              <option value="year">السنة الكاملة</option>
              <option value="first-half">النصف الأول</option>
              <option value="second-half">النصف الثاني</option>
              <option value="q1">الربع الأول</option>
              <option value="q2">الربع الثاني</option>
              <option value="q3">الربع الثالث</option>
              <option value="q4">الربع الرابع</option>
              <option value="jan">يناير</option>
              <option value="feb">فبراير</option>
              <option value="mar">مارس</option>
              <option value="apr">أبريل</option>
              <option value="may">مايو</option>
              <option value="jun">يونيو</option>
              <option value="jul">يوليو</option>
              <option value="aug">أغسطس</option>
              <option value="sep">سبتمبر</option>
              <option value="oct">أكتوبر</option>
              <option value="nov">نوفمبر</option>
              <option value="dec">ديسمبر</option>
            </select>
          </div>

          <!-- Branch Multi-Select -->
          <BranchesPicker
            v-if="branches.data.value"
            :branches="branches.data.value"
            v-model="pendingBranches"
          />

          <!-- Mosque Single-Select (dependent on branches) -->
          <MosquesPicker
            :mosques="allMosques"
            v-model="pendingMosques"
            :disabled="pendingBranches.length === 0"
          />

          <!-- Apply Button -->
          <div class="flex items-end">
            <button
              type="button"
              @click="applyPeriodSelection"
              class="w-full px-4 py-3 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-800"
            >
              تطبيق
            </button>
          </div>
        </div>
      </div>

      <!-- Statistics Sections -->
      <div class="space-y-8">
        <!-- Survey Statistics (Beneficiary Satisfaction) -->
        <div
          v-if="surveyYear && detectedPeriod && surveyStatsData?.data?.value"
          class="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-inner dark:shadow-gray-900 border border-gray-100 dark:border-gray-700/60 p-6"
        >
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-right">
            إحصائيات رضى المستفيد
          </h2>
          <PeriodCard
            :period="detectedPeriod"
            :period-data="surveyStatsData.data.value[detectedPeriod]"
            :is-main="detectedPeriod === 'year'"
            :is-dark="isDark"
          />
        </div>

        <!-- All Dashboards Stats -->
        <!-- <StatCard
          v-if="allDashboardsData?.data?.value"
          title="إحصائيات لوحة المعلومات الشاملة"
          :data="allDashboardsData.data.value"
        /> -->

        <!-- Worshippers Stats -->
        <StatCard
          v-if="worshippersStatsData?.data?.value"
          title="عدد المصلين حسب كل فرع"
          :data="worshippersStatsData.data.value"
          :is-dark="isDark"
        />

        <!-- Deadcare Stats -->
        <StatCard
          v-if="deadcareStatsData?.data?.value"
          title="أعداد الجنائز حسب كل فرع"
          :data="deadcareStatsData.data.value"
          :is-dark="isDark"
        />

        <!-- Volunteer Stats -->
        <StatCard
          v-if="volunteerStatsData?.data?.value"
          title="عدد المتطوعين حسب كل فرع"
          :data="volunteerStatsData.data.value"
          :is-dark="isDark"
        />

        <!-- Halaqat Stats -->
        <StatCard
          v-if="halaqatStatsData?.data?.value"
          title="عدد الخاتمين في كل فرع"
          :data="halaqatStatsData.data.value"
          :is-dark="isDark"
        />

        <!-- Maqari Stats -->
        <StatCard
          v-if="maqariStatsData?.data?.value"
          title="عدد المجازين حسب كل فرع"
          :data="maqariStatsData.data.value"
          :is-dark="isDark"
        />
      </div>
    </div>
  </DefaultLayout>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core'
import moment from 'moment'
import DefaultLayout from '@/layouts/Default.vue'
import StatCard from '@/components/dashboard/StatCard.vue'
import PeriodCard from '@/components/dashboard/PeriodCard.vue'

import { ref, computed, watch } from 'vue'

import { useBranches } from '@/queries/app'
import BranchesPicker from '@/components/shared/BranchesPicker.vue'
import MosquesPicker from '@/components/shared/MosquesPicker.vue'
import {
  useAllDashboards,
  useVolunteerStats,
  useWorshippersStats,
  useHalaqatStats,
  useDeadcareStats,
  useMaqariStats
} from '@/queries/dashboard'
import type { DashboardFilters } from '@/services/dashboard'
import type { Mosque } from '@/types/global'
import { fetchMosques } from '@/services/app'
import { useSurveyStatistics } from '@/queries/survey'
import { detectPeriodFromRange } from '@/services/survey'

const isDark = useDark()

// Date range and period selection
const pendingYear = ref(moment().year())
const pendingPeriod = ref('year')
const range = ref<any>([moment().startOf('year').toDate(), moment().endOf('year').toDate()])
const availableYears = computed(() => {
  const latestYear = moment().year()
  return Array.from({ length: latestYear - 2024 + 1 }, (_, index) => 2024 + index)
})

// Branch and Mosque filters (pending vs applied)
const pendingBranches = ref<number[]>([])
const pendingMosques = ref<number | null>(null)
const appliedBranches = ref<number[]>([])
const appliedMosques = ref<number | null>(null)
const allMosques = ref<Mosque[]>([])

// Fetch branches
const branches = useBranches()

// Watch for branch changes and fetch mosques (pending only)
watch(
  pendingBranches,
  async (newBranches) => {
    if (newBranches.length > 0) {
      // Clear mosque selection when branches change
      pendingMosques.value = null

      // Fetch mosques for all selected branches
      try {
        const mosquesPromises = newBranches.map((branchId) => fetchMosques(branchId))
        const mosquesResults = await Promise.all(mosquesPromises)
        allMosques.value = mosquesResults.flat()
      } catch (error) {
        console.error('Failed to fetch mosques:', error)
        allMosques.value = []
      }
    } else {
      allMosques.value = []
      pendingMosques.value = null
    }
  },
  { immediate: false }
)

// Handle period selection and apply on demand
const applyPeriodSelection = () => {
  const startOfYear = moment({ year: pendingYear.value }).startOf('year')
  const endOfYear = moment({ year: pendingYear.value }).endOf('year')

  switch (pendingPeriod.value) {
    case 'all':
      range.value = [false, false]
      break
    case 'year':
      range.value = [startOfYear.toDate(), endOfYear.toDate()]
      break
    case 'first-half':
      range.value = [
        startOfYear.toDate(),
        moment({ year: pendingYear.value, month: 5 }).endOf('month').toDate()
      ]
      break
    case 'second-half':
      range.value = [
        moment({ year: pendingYear.value, month: 6 }).startOf('month').toDate(),
        endOfYear.toDate()
      ]
      break
    case 'q1':
      range.value = [
        startOfYear.toDate(),
        moment({ year: pendingYear.value, month: 2 }).endOf('month').toDate()
      ]
      break
    case 'q2':
      range.value = [
        moment({ year: pendingYear.value, month: 3 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 5 }).endOf('month').toDate()
      ]
      break
    case 'q3':
      range.value = [
        moment({ year: pendingYear.value, month: 6 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 8 }).endOf('month').toDate()
      ]
      break
    case 'q4':
      range.value = [
        moment({ year: pendingYear.value, month: 9 }).startOf('month').toDate(),
        endOfYear.toDate()
      ]
      break
    case 'jan':
      range.value = [
        moment({ year: pendingYear.value, month: 0 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 0 }).endOf('month').toDate()
      ]
      break
    case 'feb':
      range.value = [
        moment({ year: pendingYear.value, month: 1 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 1 }).endOf('month').toDate()
      ]
      break
    case 'mar':
      range.value = [
        moment({ year: pendingYear.value, month: 2 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 2 }).endOf('month').toDate()
      ]
      break
    case 'apr':
      range.value = [
        moment({ year: pendingYear.value, month: 3 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 3 }).endOf('month').toDate()
      ]
      break
    case 'may':
      range.value = [
        moment({ year: pendingYear.value, month: 4 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 4 }).endOf('month').toDate()
      ]
      break
    case 'jun':
      range.value = [
        moment({ year: pendingYear.value, month: 5 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 5 }).endOf('month').toDate()
      ]
      break
    case 'jul':
      range.value = [
        moment({ year: pendingYear.value, month: 6 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 6 }).endOf('month').toDate()
      ]
      break
    case 'aug':
      range.value = [
        moment({ year: pendingYear.value, month: 7 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 7 }).endOf('month').toDate()
      ]
      break
    case 'sep':
      range.value = [
        moment({ year: pendingYear.value, month: 8 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 8 }).endOf('month').toDate()
      ]
      break
    case 'oct':
      range.value = [
        moment({ year: pendingYear.value, month: 9 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 9 }).endOf('month').toDate()
      ]
      break
    case 'nov':
      range.value = [
        moment({ year: pendingYear.value, month: 10 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 10 }).endOf('month').toDate()
      ]
      break
    case 'dec':
      range.value = [
        moment({ year: pendingYear.value, month: 11 }).startOf('month').toDate(),
        moment({ year: pendingYear.value, month: 11 }).endOf('month').toDate()
      ]
      break
  }

  // Apply branch/mosque selections only on apply
  appliedBranches.value = [...pendingBranches.value]
  appliedMosques.value = pendingMosques.value
}

// Build dashboard filters
const dashboardFilters = computed<DashboardFilters>(() => ({
  branch: appliedBranches.value,
  mosque: appliedMosques.value !== null ? [appliedMosques.value] : [],
  date_from: range.value?.[0] ? moment(range.value[0]).format('YYYY-MM-DD') : false,
  date_to: range.value?.[1] ? moment(range.value[1]).format('YYYY-MM-DD') : false,
  group_by_mosque: appliedMosques.value !== null,
  group_by_branch: appliedBranches.value.length > 0
}))

// Survey statistics - detect period from date range
const surveyYear = computed(() => {
  if (!range.value?.[0]) return null
  const year = moment(range.value[0]).year()
  // Only show survey stats for 2024 onwards
  return year >= 2024 ? year : null
})

const detectedPeriod = computed(() => {
  if (!range.value?.[0] || !range.value?.[1]) return null
  return detectPeriodFromRange(range.value[0], range.value[1])
})

// Survey statistics filters for branch/mosque
const surveyBranch = computed(() => {
  // Only pass single branch if exactly one is selected and no mosque
  return appliedBranches.value.length === 1 && appliedMosques.value === null
    ? appliedBranches.value[0]
    : undefined
})

const surveyMosque = computed(() => {
  // Return the mosque if one is selected
  return appliedMosques.value ?? undefined
})

// Fetch survey statistics
const surveyStatsData = useSurveyStatistics(surveyYear, surveyBranch, surveyMosque)

// Fetch all dashboard statistics
const allDashboardsData = useAllDashboards(dashboardFilters)
const volunteerStatsData = useVolunteerStats(dashboardFilters)
const worshippersStatsData = useWorshippersStats(dashboardFilters)
const halaqatStatsData = useHalaqatStats(dashboardFilters)
const deadcareStatsData = useDeadcareStats(dashboardFilters)
const maqariStatsData = useMaqariStats(dashboardFilters)
</script>

<style scoped></style>
